/**
 * Primes endpoints of the extranet (fiches 22749 / 22763 / 22988).
 *
 * - `boby-ws2-ea-tab-qui` (`WS2_EA_TAB_QUI`): the premiums (quittances) of
 *   the session — every row in one call (`perPage` defaults to `9999`
 *   server-side), filtered server-side (fiche 22988) by `filtre` (the
 *   « Rechercher par réf, produit, assuré… » text) and `situation` (a `VAL`
 *   of `WS2_EA_SEL_STATUT_PRIME`: `PAYEE` / `IMPAYEE`). Both are **not sent**
 *   when blank (« Toutes situations »).
 * - `boby-ws2-ea-sel-statut-prime` (`WS2_EA_SEL_STATUT_PRIME`): the options
 *   of the situation select (`{ VAL, LIB }`, API order).
 * - `boby-ws2-ea-tab-docs-qui` (`WS2_EA_TAB_DOCS_QUI`): the documents of one
 *   premium (`id_qui` = `ID_QUI` of the list row) — the side panel
 *   « Documents — {NUM_QUI} ».
 *
 * Raw VEOS rows are typed as returned and mapped to the view models the cards
 * render (stable keys = the cards' column / item keys). `null` stays `null`
 * (the cards render it as an empty value, never the text "null").
 */

import type { DocumentItem } from '../components/shared';
import { formatMsDate, toFiniteNumber, type VeosScalar } from '../utils/format';
import { fetchErpRows } from './erpProxy';
import { buildFilterParams, fetchSelOptions, type SelOption } from './listFilters';

/** Endpoint slugs (contract §6). */
export const TAB_QUI_ENDPOINT = 'boby-ws2-ea-tab-qui';
export const TAB_DOCS_QUI_ENDPOINT = 'boby-ws2-ea-tab-docs-qui';
/** `WS2_EA_SEL_STATUT_PRIME` — options of the situation select (fiche 22988). */
export const SEL_STATUT_PRIME_ENDPOINT = 'boby-ws2-ea-sel-statut-prime';

// ---------------------------------------------------------------------------
// List filters (fiche 22988)
// ---------------------------------------------------------------------------

/** Server-side filters of « Mes primes »: both omitted from the request when blank. */
export interface PremiumsListFilters {
  /** Free-text search (réf, produit, assuré…); blank ⇒ not sent. */
  filtre?: string | null;
  /** `PAYEE` / `IMPAYEE` (`VAL` of `WS2_EA_SEL_STATUT_PRIME`); blank ⇒ « Toutes situations ». */
  situation?: string | null;
}

/** Request params of `WS2_EA_TAB_QUI`: `filtre` / `situation` only when non-blank (trimmed). */
export function buildPremiumsListParams(
  filters: PremiumsListFilters = {},
): Record<string, string> {
  return buildFilterParams({ filtre: filters.filtre, situation: filters.situation });
}

// ---------------------------------------------------------------------------
// Raw VEOS rows
// ---------------------------------------------------------------------------

/** One `WS2_EA_TAB_QUI` row (fiche 22749). */
export interface PremiumRow {
  ID_POL: number | null;
  /** Premium reference (« Réf. prime »). */
  NUM_QUI: string | null;
  /** Contract number. */
  NUM_POL: string | null;
  /** Insured name. */
  NOM2_PER: string | null;
  /** Insurer — not displayed (fiche). */
  NOM_CIE: string | null;
  /** Amount incl. taxes, euros. */
  TTC_QUI: number | string | null;
  /** Period start — ms timestamp. */
  DATDEB_QUI: number | string | null;
  /** Period end — ms timestamp. */
  DTFIN_QUI: number | string | null;
  DATEMI_QUI: number | null;
  /** Situation, displayed verbatim as a badge (« Payée » / « Impayée »). */
  QUI_SITUATION: string | null;
  LIB_SQ: string | null;
  DATREG_QUI: number | null;
  /** Product label. */
  LIB_PROD: string | null;
  ID_PER: number | null;
  ID_NPOL: number | null;
  /** Premium id — the `id_qui` of its documents. */
  ID_QUI: number | null;
}

/** One `WS2_EA_TAB_DOCS_QUI` row (fiche 22763). */
export interface PremiumDocumentRow {
  /** Document name. */
  LIB_ADOC: string | null;
  /** Document date — ms timestamp. */
  DAT_ADOC: number | string | null;
  ID_ADOC: number | null;
  /** Document category. */
  LIB_DS: string | null;
  /** « Document » or « Email » — not displayed. */
  TYPE: string | null;
  /** Premium reference (panel title). */
  NUM_QUI: string | null;
}

// ---------------------------------------------------------------------------
// View models
// ---------------------------------------------------------------------------

/** One premium as the « Mes primes » list renders it. */
export interface Premium {
  /** Stable row id (`ID_QUI`, else `NUM_QUI`, else the row index). */
  id: string;
  /** `ID_QUI` — the documents' `id_qui`; `null` ⇒ no documents to fetch. */
  idQui: number | null;
  /** `NOM2_PER`, trimmed. */
  assure: string | null;
  /** `LIB_PROD`, trimmed. */
  produit: string | null;
  /** `NUM_POL`, trimmed. */
  numeroContrat: string | null;
  /** `NUM_QUI`, trimmed. */
  reference: string | null;
  /** `DATDEB_QUI` — raw ms timestamp (rendered `JJ/MM/AAAA`, sorts chronologically). */
  periodeDu: number | string | null;
  /** `DTFIN_QUI` — raw ms timestamp. */
  periodeAu: number | string | null;
  /** `TTC_QUI` in euros. */
  ttc: number | null;
  /** `QUI_SITUATION` exactly as returned. */
  situation: string | null;
}

/** One document of a premium (shared document view model + its `NUM_QUI`). */
export interface PremiumDocument extends DocumentItem {
  /** `NUM_QUI` returned with the document. */
  numQui: string | null;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

/** Trimmed text, `null` for a missing / blank value. */
function textOrNull(value: VeosScalar): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}

/** A VEOS date kept raw: a ms number (or numeric string → number), a pre-formatted string, or `null`. */
function rawDate(value: number | string | null | undefined): number | string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const numeric = toFiniteNumber(value);
  if (numeric !== null) return numeric;
  return textOrNull(value);
}

/** Map a `WS2_EA_TAB_QUI` row to the list view model. */
export function mapPremiumRow(row: PremiumRow, index: number): Premium {
  const idQui = toFiniteNumber(row.ID_QUI);
  const reference = textOrNull(row.NUM_QUI);
  return {
    id: idQui === null ? (reference ?? `row-${String(index)}`) : String(idQui),
    idQui,
    assure: textOrNull(row.NOM2_PER),
    produit: textOrNull(row.LIB_PROD),
    numeroContrat: textOrNull(row.NUM_POL),
    reference,
    periodeDu: rawDate(row.DATDEB_QUI),
    periodeAu: rawDate(row.DTFIN_QUI),
    ttc: toFiniteNumber(row.TTC_QUI),
    situation: row.QUI_SITUATION ?? null,
  };
}

/** Map a `WS2_EA_TAB_DOCS_QUI` row to the document view model. */
export function mapPremiumDocumentRow(row: PremiumDocumentRow, index: number): PremiumDocument {
  const idAdoc = toFiniteNumber(row.ID_ADOC);
  const rawDocDate = rawDate(row.DAT_ADOC);
  const date = typeof rawDocDate === 'number' ? formatMsDate(rawDocDate) : (rawDocDate ?? '');
  return {
    id: idAdoc === null ? `doc-${String(index)}` : String(idAdoc),
    nom: textOrNull(row.LIB_ADOC),
    categorie: textOrNull(row.LIB_DS),
    date: date === '' ? null : date,
    numQui: textOrNull(row.NUM_QUI),
  };
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/**
 * The premiums of the session (`WS2_EA_TAB_QUI`), in API order, filtered
 * server-side by `filtre` / `situation` (each sent only when non-blank).
 */
export async function fetchPremiums(
  registrySlug: string | null | undefined,
  filters: PremiumsListFilters = {},
): Promise<Premium[]> {
  const rows = await fetchErpRows<PremiumRow>(
    registrySlug,
    TAB_QUI_ENDPOINT,
    buildPremiumsListParams(filters),
  );
  return rows.map(mapPremiumRow);
}

/** Options of the situation select (`WS2_EA_SEL_STATUT_PRIME`), API order. */
export async function fetchPremiumSituationOptions(
  registrySlug: string | null | undefined,
): Promise<SelOption[]> {
  return fetchSelOptions(registrySlug, SEL_STATUT_PRIME_ENDPOINT);
}

/** The documents of a premium (`WS2_EA_TAB_DOCS_QUI?id_qui=…`), in API order. */
export async function fetchPremiumDocuments(
  registrySlug: string | null | undefined,
  idQui: number,
): Promise<PremiumDocument[]> {
  const rows = await fetchErpRows<PremiumDocumentRow>(registrySlug, TAB_DOCS_QUI_ENDPOINT, {
    id_qui: idQui,
  });
  return rows.map(mapPremiumDocumentRow);
}

export const premiumsService = {
  fetchPremiums,
  fetchPremiumSituationOptions,
  fetchPremiumDocuments,
};
